import React, { useMemo, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NewsList from "./NewsList";
import { useTranslations } from "src/contexts/TranslationContext";
import RobotsNoIndex from "src/components/seo/RobotsNoIndex";
import { useCanonicalTag } from "src/components/seo/useCanonicalTag";

const News: React.FC = () => {
  // Get path params: lang, type, optional subtype
  const { lang, type: pathType, subtype: pathSubtype } = useParams<{
    lang: string;
    type: string;
    subtype?: string;
  }>();

  // Track mounted state
  const isMounted = useRef(true);
  const lastTypeRef = useRef<string>('');
  const lastSubtypeRef = useRef<string | undefined>(undefined);
  const lastLocaleRef = useRef<string>('');

  // Use translations context
  const { language: contextLang, translations: t, getUrlLanguagePrefix } = useTranslations();

  // Use lang param if exists, else fallback to context language
  const finalLocale = lang || contextLang;

  // Memoize final type and subtype from path params (or fallback to 'trending' if missing)
  const finalType = useMemo(() => pathType || 'trending', [pathType]);
  const finalSubtype = useMemo(() => pathSubtype || undefined, [pathSubtype]);

  // Add or update canonical tag
  useCanonicalTag();

  // Log when type/subtype/locale changes
  useEffect(() => {
    if (
      finalType !== lastTypeRef.current ||
      finalSubtype !== lastSubtypeRef.current ||
      finalLocale !== lastLocaleRef.current
    ) {
      console.log(
        `News component actual change - type: ${finalType} (was: ${lastTypeRef.current}), subtype: ${finalSubtype} (was: ${lastSubtypeRef.current}), language: ${finalLocale} (was: ${lastLocaleRef.current})`
      );
      lastTypeRef.current = finalType;
      lastSubtypeRef.current = finalSubtype;
      lastLocaleRef.current = finalLocale;
    }
  }, [finalType, finalSubtype, finalLocale]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const navigate = useNavigate();

  // Get translated type name
  const getTypeTranslation = (type: string) => {
    if (type === 'trending') return t?.navigations?.header?.trending || 'Top News';
    if (type === 'entertainment') return t?.navigations?.header?.entertainment || 'Entertainment';
    if (type === 'sports') return t?.navigations?.header?.sports || 'Sports';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Navigation handlers
  const handleTypeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const path = `/news/list/${finalType}`;
    const fullPath = `${getUrlLanguagePrefix(finalLocale)}${path}`;
    navigate(fullPath);
  };

  const handleSubtypeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!finalSubtype) return;
    const path = `/news/list/${finalType}/${finalSubtype}`;
    const fullPath = `${getUrlLanguagePrefix(finalLocale)}${path}`;
    navigate(fullPath);
  };

  return (
    <div>
      {finalSubtype && <RobotsNoIndex />}

      <div className="bg-white border-b border-gray-200 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-2xl font-bold capitalize text-gray-800 flex items-center space-x-3">
            {finalSubtype ? (
              <>
                <span
                  onClick={handleTypeClick}
                  className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium hover:underline inline-flex items-center"
                >
                  {getTypeTranslation(finalType)}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
                <span className="text-gray-400">/</span>
                <span
                  onClick={handleSubtypeClick}
                  className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium hover:underline inline-flex items-center"
                >
                  {t?.navigations?.header?.[finalSubtype.toLowerCase()] || finalSubtype}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </>
            ) : (
              <h1
                onClick={handleTypeClick}
                className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium hover:underline inline-flex items-center"
              >
                {getTypeTranslation(finalType)}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </h1>
            )}
          </h1>
        </div>
      </div>

      <NewsList
        lang={finalLocale}
        type={finalType}
        subtype={finalSubtype}
        language={finalLocale}
        t={t}
        getUrlLanguagePrefix={getUrlLanguagePrefix}
      />
    </div>
  );
};

export default News;
